/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect } from "react";

import { MuiPickersUtilsProvider, DatePicker } from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";

import {
  ArrayInput,
  FormDataConsumer,
  minValue,
  number,
  NumberInput,
  required,
  SelectInput,
  SimpleFormIterator,
  TextInput,
} from "react-admin";
import { useForm } from "react-final-form";

import { Grid, Typography, InputAdornment, TextField } from "@material-ui/core";

import { ServiceType, Unit } from "../../../utils/constants";
import {
  formatCurrency,
  taxRateRenderer,
  trimDecimal,
} from "../../../utils/utils";

import { theme } from "../../../themes";
import { CalendarIcon } from "../../../components/common/iconography";

import { getUnitPrice } from "../services/ServiceUtils";
import { getUnitChoices } from "./InvoiceUtils";

const validateRequired = required();
const validateService = required("Select Service...");
const validateQuantity = [required(), number(), minValue(0)];

const InvoiceTotals = ({ formData, form, ...props }) => {
  const ast = window.appUser?.reloadYard?.ast;
  const initTotal = form.getFieldState("total")?.initial || 0;
  const initBalance = form.getFieldState("balance")?.initial || 0;
  const paid = initTotal - initBalance;
  const calcTotals = (lineItems) => {
    const subtotal = (lineItems?.items || [])
      .map((item) => Number(item?.amount) || 0)
      .reduce((accum, curr) => accum + curr, 0);

    let taxAmount = null;
    if (!ast) {
      taxAmount = (lineItems?.items || [])
        .map((item) => Number(item?.itemTax) || 0)
        .reduce((accum, curr) => accum + curr, 0);
    }

    const total = subtotal + (taxAmount || 0);
    let balance = null;
    if (!ast) balance = trimDecimal(total - paid);

    form.change("subtotal", subtotal);
    form.change("tax", taxAmount);
    form.change("total", total);
    form.change("balance", balance);
  };

  useEffect(() => {
    const lineItemsState = form.getFieldState("lineItems.items");
    const itemsDirty = lineItemsState?.dirty;
    const hasLineItems = !!formData.lineItems?.items?.length;

    // Only recalculate when we actually need to:
    // - Create (no id): always derive totals from current line items
    // - Edit (has id): only after user modifies line items (dirty)
    //    Otherwise keep DB-loaded values set from external zapier integration
    if (!hasLineItems) return;

    if (!formData.id) {
      // Create: always calc total from current items
      calcTotals(formData.lineItems);
    } else if (itemsDirty) {
      // Edit: recalc total only if lineItems have been modified
      calcTotals(formData.lineItems);
    }
  }, [formData.lineItems]);

  return (
    <Grid container direction="row" spacing={1}>
      <Grid item xs={12}>
        <Typography
          variant="overline"
          color="textSecondary"
          gutterBottom={true}
        >
          Totals:
        </Typography>
      </Grid>
      <Grid item xs={6} sm={3}>
        <NumberInput
          source="subtotal"
          label="Subtotal"
          variant="outlined"
          fullWidth
          disabled
          format={(v) => Number(v).toFixed(2)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start"> $ </InputAdornment>
            ),
            inputMode: "decimal",
          }}
        />
      </Grid>
      <Grid item xs={6} sm={3}>
        <NumberInput
          source="tax"
          label="Taxes"
          variant="outlined"
          fullWidth
          disabled
          defaultValue={ast ? null : 0}
          format={(v) => (v != null ? Number(v).toFixed(2) : null)}
          placeholder={ast ? "---" : "0.00"}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start"> $ </InputAdornment>
            ),
            inputMode: "decimal",
          }}
        />
      </Grid>
      <Grid item xs={6} sm={3}>
        <NumberInput
          source="total"
          label="Total Charges"
          variant="outlined"
          fullWidth
          disabled
          defaultValue={0}
          format={(v) => Number(v).toFixed(2)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start"> $ </InputAdornment>
            ),
            inputMode: "decimal",
          }}
        />
      </Grid>
      {formData?.id ? (
        <Grid item xs={6} sm={3}>
          <NumberInput
            source="balance"
            label="Balance"
            variant="outlined"
            fullWidth
            disabled
            defaultValue={ast ? null : 0}
            format={(v) => (v != null ? Number(v).toFixed(2) : null)}
            placeholder={ast ? "---" : "0.00"}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"> $ </InputAdornment>
              ),
              inputMode: "decimal",
            }}
          />
        </Grid>
      ) : null}
    </Grid>
  );
};

export const ChargesTab = ({
  account,
  services,
  isSyncEnabled,
  isLocked,
  ...props
}) => {
  const form = useForm();
  const taxRateChoices = window.appUser?.reloadYard?.taxRates
    ? window.appUser.reloadYard.taxRates
    : [{}];
  const ast = window.appUser?.reloadYard?.ast;

  const handleServiceChange = (event, scopedFormData, getSource) => {
    debugger;
    if (event.target.value && services) {
      let service = services.find((itm) => itm.id === event.target.value);
      form.change(getSource("taxRate"), service.taxRate);
      form.change(getSource("service"), { name: service.name });

      if (service.name.includes(ServiceType.ACCESSORIAL_CHARGES)) {
        form.change(getSource("quantity"), 1);
        form.change(getSource("units"), Unit.PCS);
        form.change(getSource("unitCost"), 0);
        form.change(getSource("amount"), 0);
        form.change(getSource("itemTax"), 0);
      } else if (scopedFormData && scopedFormData.units) {
        form.change(
          getSource("unitCost"),
          getUnitPrice(service, scopedFormData.units, 0),
        );
        if (scopedFormData.quantity) {
          const itemAmount =
            scopedFormData.quantity *
            getUnitPrice(service, scopedFormData.units, 0);
          form.change(getSource("amount"), itemAmount);
          form.change(
            getSource("itemTax"),
            calcItemTax(itemAmount, service.taxRate),
          );
        } else {
          form.change(getSource("amount"), 0);
          form.change(getSource("itemTax"), 0);
        }
      }
    }
  };

  const handleUnitChange = (event, scopedFormData, getSource) => {
    debugger;
    if (event.target.value && services && scopedFormData?.serviceId) {
      let service = services.find((itm) => itm.id === scopedFormData.serviceId);
      const pricePerUnit = getUnitPrice(service, event.target.value, 0);
      form.change(getSource("unitCost"), pricePerUnit);
      if (scopedFormData.quantity) {
        const itemAmount = scopedFormData.quantity * pricePerUnit;
        form.change(getSource("amount"), itemAmount);
        form.change(
          getSource("itemTax"),
          calcItemTax(itemAmount, service.taxRate),
        );
      } else {
        form.change(getSource("amount"), 0);
        form.change(getSource("itemTax"), 0);
      }
    }
  };

  const calcItemTax = (amount, taxCode) => {
    debugger;
    if (amount && taxCode) {
      let taxCodeRate = null;
      if (typeof taxCode === "string")
        taxCodeRate = window.appUser.reloadYard.taxRates.find(
          (r) => r.taxCode === taxCode,
        );
      else taxCodeRate = taxCode;
      const taxRate = taxCodeRate ? taxCodeRate.taxRate / 100 : 0;
      return amount * taxRate;
    } else return 0;
  };

  const handleTaxChange = (event, scopedFormData, getSource) => {
    form.change(
      getSource("itemTax"),
      calcItemTax(scopedFormData.amount, event.target.value),
    );
  };

  const handleQuantityChange = (event, scopedFormData, getSource) => {
    debugger;
    if (event.target.value && scopedFormData?.unitCost) {
      const itemAmount = event.target.value * scopedFormData.unitCost;
      form.change(getSource("amount"), itemAmount);
      form.change(
        getSource("itemTax"),
        calcItemTax(itemAmount, scopedFormData.taxRate),
      );
    } else {
      form.change(getSource("amount"), 0);
      form.change(getSource("itemTax"), 0);
    }
  };

  const handleUnitCostChange = (event, scopedFormData, getSource) => {
    debugger;
    if (event.target.value && scopedFormData.quantity) {
      const itemAmount = event.target.value * scopedFormData.quantity;
      form.change(getSource("amount"), itemAmount);
      form.change(
        getSource("itemTax"),
        calcItemTax(itemAmount, scopedFormData.taxRate),
      );
    } else {
      form.change(getSource("amount"), 0);
      form.change(getSource("itemTax"), 0);
    }
  };

  const handleServiceDateChange = (value, source) => {
    const lineItemsState = form.getFieldState("lineItems.items");
    const lineItems = lineItemsState?.value;
    if (lineItems) form.change(`${source}serviceDate`, value);
  };

  const getLineTotal = (scopedFormData) => {
    const subtotal = scopedFormData?.amount ?? 0;
    const tax = scopedFormData?.itemTax ?? 0;
    const lineTotal = Number(subtotal + tax).toFixed(2);
    return lineTotal;
  };

  const getTaxHelperText = (scopedFormData) => {
    if (ast) return false;
    return `Tax Amount: ${formatCurrency(scopedFormData?.itemTax)}`;
  };

  return (
    <Grid container direction="row" spacing={3}>
      <Grid item xs={12}>
        <ArrayInput
          source="lineItems.items"
          label="SERVICE ITEMS"
          validate={isSyncEnabled ? validateRequired : undefined}
          fullWidth
        >
          <SimpleFormIterator
            disableReordering
            disableAdd={!account || isLocked}
            disableRemove={isLocked}
          >
            <FormDataConsumer>
              {({
                formData, // The whole form data
                scopedFormData, // The data for this item of the ArrayInput
                getSource, // Function to get the valid source inside an ArrayInput. Translates to "inventories.items[].property"
                ...rest
              }) => (
                <Grid container direction="row" spacing={1}>
                  <Grid item xs={12} lg={4}>
                    <SelectInput
                      source={getSource("serviceId")}
                      label="Service"
                      variant="outlined"
                      fullWidth
                      disabled={!services?.length || isLocked}
                      choices={services ? services : []}
                      onChange={(e) =>
                        handleServiceChange(e, scopedFormData, getSource)
                      }
                      validate={validateService}
                      helperText={false}
                    />
                  </Grid>
                  <Grid item xs={12} lg={8}>
                    <TextInput
                      source={getSource("serviceNote")}
                      label="Service Note"
                      disabled={!scopedFormData?.serviceId || isLocked}
                      variant="outlined"
                      fullWidth
                      helperText={false}
                    />
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <MuiPickersUtilsProvider utils={DateFnsUtils}>
                      <DatePicker
                        label="Service Date"
                        margin="dense"
                        inputVariant="outlined"
                        variant="dialog"
                        clearable
                        fullWidth
                        disableFuture
                        helperText={false}
                        value={
                          scopedFormData?.serviceDate
                            ? scopedFormData.serviceDate
                            : null
                        }
                        format="MM/dd/yyyy" // display format
                        onChange={(value) =>
                          handleServiceDateChange(value, getSource(""))
                        }
                        disabled={isLocked}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <CalendarIcon />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </MuiPickersUtilsProvider>
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <NumberInput
                      source={getSource("quantity")}
                      label="Quantity"
                      variant="outlined"
                      fullWidth
                      disabled={!scopedFormData?.serviceId || isLocked}
                      min="1"
                      validate={validateQuantity}
                      onChange={(e) =>
                        handleQuantityChange(e, scopedFormData, getSource)
                      }
                    />
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <SelectInput
                      source={getSource("units")}
                      label="Units"
                      variant="outlined"
                      fullWidth
                      style={{ minWidth: theme.spacing(9) }}
                      disabled={!scopedFormData?.serviceId || isLocked}
                      choices={getUnitChoices(scopedFormData, services)}
                      onChange={(e) =>
                        handleUnitChange(e, scopedFormData, getSource)
                      }
                      validate={validateRequired}
                    />
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <NumberInput
                      source={getSource("unitCost")}
                      label="Unit Rate"
                      variant="outlined"
                      fullWidth
                      disabled={
                        !scopedFormData?.serviceId ||
                        !scopedFormData?.units ||
                        isLocked
                      }
                      min="0"
                      placeholder="0.00"
                      validate={validateQuantity}
                      onChange={(e) =>
                        handleUnitCostChange(e, scopedFormData, getSource)
                      }
                      format={(v) => (Number(v) === 0 ? null : v)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start"> $ </InputAdornment>
                        ),
                        inputMode: "decimal",
                      }}
                      helperText={`Subtotal: ${formatCurrency(
                        scopedFormData?.amount,
                      )}`}
                    />
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <SelectInput
                      source={getSource("taxRate")}
                      label="Tax"
                      choices={taxRateChoices}
                      optionValue="taxCode"
                      optionText={taxRateRenderer}
                      // format&parse based on taxCode, while value = { taxRate }
                      format={(v) => (!v ? "" : v.taxCode)}
                      parse={(v) =>
                        !v ? "" : taxRateChoices.find((r) => r.taxCode === v)
                      }
                      onChange={(e) =>
                        handleTaxChange(e, scopedFormData, getSource)
                      }
                      validate={validateRequired}
                      variant="outlined"
                      fullWidth
                      disabled={isLocked}
                      style={{ minWidth: theme.spacing(9) }}
                      helperText={getTaxHelperText(scopedFormData)}
                    />
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <TextField
                      label="Line Total"
                      variant="outlined"
                      margin="dense"
                      fullWidth
                      disabled={true}
                      value={getLineTotal(scopedFormData)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start"> $ </InputAdornment>
                        ),
                        inputMode: "decimal",
                      }}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                </Grid>
              )}
            </FormDataConsumer>
          </SimpleFormIterator>
        </ArrayInput>
      </Grid>
      <Grid item xs={12}>
        <FormDataConsumer>
          {(formDataProps) => <InvoiceTotals {...formDataProps} form={form} />}
        </FormDataConsumer>
      </Grid>
    </Grid>
  );
};

export default ChargesTab;
